import React, { forwardRef } from 'react'
import {
  TextField,
  Grid2,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  Box
} from '@mui/material'
import { Formik, FormikProps, Form, Field } from 'formik'
import * as Yup from 'yup'
import { useApi } from '../../../hooks/useApi'
import { createOrderOrigin, updateOrderOrigin } from '../config/api'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import {
  OrderOrigin,
  OrderOriginInfoDto
} from '../../../interfaces/OrderOrigin/OrderOrigin'

interface CreateEditOrderOriginModalProps {
  orderOrigin?: OrderOriginInfoDto
  isEditing?: boolean
  closeModal: () => void
  onSuccess: () => void
  handleSnackbar: (message: string, success: boolean) => void
}

const validationSchema = Yup.object().shape({
  origin: Yup.string()
    .trim()
    .min(1, 'Canal de venta es requerido')
    .max(50, 'Canal de venta no puede tener más de 50 caracteres')
    .required('Canal de venta es requerido')
})

const initialValues: OrderOrigin = {
  orderOriginId: 0,
  origin: '',
  active: true
}

export const CreateEditOrderOriginModal = forwardRef<
  FormikProps<OrderOrigin>,
  CreateEditOrderOriginModalProps
>(({ orderOrigin, isEditing = false, closeModal, onSuccess, handleSnackbar }, ref) => {
  const { callApi, state } = useApi()

  const normalizedInitialValues: OrderOrigin = orderOrigin
    ? {
        orderOriginId: orderOrigin.orderOriginId,
        origin: orderOrigin.origin,
        active: orderOrigin.active
      }
    : initialValues

  const handleSubmit = (values: OrderOrigin) => {
    const submitData: OrderOrigin = {
      orderOriginId: values.orderOriginId,
      origin: values.origin.trim(),
      active: values.active
    }

    const apiCall = isEditing
      ? updateOrderOrigin(submitData.orderOriginId, submitData)
      : createOrderOrigin(submitData)

    return callApi(apiCall)
      .then(() => {
        const message = isEditing
          ? 'Canal de venta actualizado correctamente'
          : 'Canal de venta creado correctamente'
        handleSnackbar(message, true)
        onSuccess()
        closeModal()
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
  }

  if (state.status === Status.Loading)
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )

  return (
    <Formik
      innerRef={ref}
      initialValues={normalizedInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
        <Form>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Typography variant='subtitle2' fontWeight={600} gutterBottom>
                Canal de Venta (ClientAlias)
              </Typography>
              <Field
                as={TextField}
                name='origin'
                placeholder='Ingrese el nombre del canal de venta...'
                fullWidth
                variant='outlined'
                value={values.origin}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.origin && Boolean(errors.origin)}
                disabled={isSubmitting}
                required
              />
              <Typography variant='caption' color='text.secondary'>
                {touched.origin && errors.origin
                  ? errors.origin
                  : 'Nombre con el que se identificará el canal de venta en el sistema.'}
              </Typography>
            </Grid2>

            <Grid2 size={12}>
              <Typography variant='subtitle2' fontWeight={600} gutterBottom>
                Estado del canal de venta
              </Typography>
              <FormControlLabel
                control={
                  <Field
                    as={Checkbox}
                    name='active'
                    checked={values.active}
                    onChange={handleChange}
                    color='primary'
                    disabled={isSubmitting}
                  />
                }
                label='Activo'
              />
            </Grid2>

            {isEditing && (
              <Grid2 size={12}>
                <Alert severity='warning' sx={{ alignItems: 'center' }}>
                  <Box component='ul' sx={{ m: 0, pl: 3 }}>
                    <li>Este cambio aplicará solo a los pedidos nuevos.</li>
                    <li>Los pedidos que ya fueron creados con la configuración anterior no se verán afectados y mantendrán el canal de venta original.</li>
                  </Box>
                </Alert>
              </Grid2>
            )}
          </Grid2>
        </Form>
      )}
    </Formik>
  )
})
